/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { activityApiService } from '~/services/api/api.activity'
import { useTranslation } from 'react-i18next'
import { RcmTableDataWrapper } from '~/components/rcm-table'
import { usersApiService } from '~/services/api'
import EditUserModal from '../system/users/edit/Modal'
import RcmTableFilterTrigger from '~/components/rcm-table/RcmTableFilterTrigger'
import TableFilter from './TableFilter'
import UserActivityTable from './Table'

interface User {
  firstName?: string
  lastName?: string
  email?: string
  enabled?: boolean
  locked?: boolean
  accountName?: string
  title?: string
  department?: string
  role?: string
  fullName?: string
  expiresAt?: string | number
}

const UserActivity: React.FC = () => {
  const persistenceKey = 'systemUsers'
  const { t: translate } = useTranslation()
  const { userId } = useParams()
  const [user, setUser] = useState<User>({})
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  useEffect(() => {
    if (userId) {
      usersApiService.getById(Number(userId)).then((data: any) => {
        setUser(data)
      })
    }
  }, [userId])

  const loadData = async (queryParams: any, filter: any) => {
    const params: any = { ...queryParams }
    if (Array.isArray(filter?.actions)) {
      params.action = filter.actions.map((item: any) => item)
    }
    if (filter?.from instanceof Date) {
      params.createdAtFrom = filter.from.getTime()
    }
    if (filter?.to instanceof Date) {
      params.createdAtTo = filter.to.getTime()
    }
    const result = await activityApiService.getUserAuditLogs(Number(userId), params)
    return {
      elements: result.elements,
      totalElements: result.totalElements ?? 0,
      hasNextPage: result.hasNextPage
    }
  }

  return (
    <RcmTableDataWrapper
      loadData={loadData}
      defaultSortBy={'NAME'}
      defaultSortDesc={false}
      persistenceKey={persistenceKey}
    >
      <div className="d-flex flex-wrap">
        <div className="align-left d-flex">
          <h1 className="d-inline-block" id="11fcf2a0-a44c-412e-ae9d-0412651e27c1">
            {translate('USERS_ACTIVITY_H_TITLE')}
          </h1>
        </div>

        <div className="align-right align-bottom d-flex">
          <RcmTableFilterTrigger />
        </div>
      </div>

      <div>
        <span>
          <Link className="d-inline" to="/system/users">
            {translate('USERS_TITLE')}
          </Link>
        </span>
        {user && userId && (
          <>
            <span>&nbsp; / &nbsp;</span>
            <span>
              <Link to="#" onClick={() => setShowEditModal(true)}>
                {user?.fullName}
              </Link>
            </span>
          </>
        )}
      </div>
      <div>
        <TableFilter />
        <UserActivityTable />
      </div>

      {showEditModal && userId && user && (
        <EditUserModal
          userId={Number(userId)}
          onClose={() => {
            setShowEditModal(false)
          }}
        />
      )}
    </RcmTableDataWrapper>
  )
}

export default UserActivity
